import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import { formatClock } from "../attachments/VoiceNote";
import { SegmentedControl } from "../components/SegmentedControl";
import type { CallSnapshot } from "../lib/callSession";
import type { TtsVoice } from "../lib/speech";
import "./call.css";

type ModelChoice = "reason" | "fast";

/**
 * Sesli gorusme ekrani - tam ekran, telefon aramasi gibi.
 *
 * Ortada bir KURE durumu anlatiyor: dinlerken sesinizle buyuyor, dusunurken
 * yavasca nabiz atiyor, konusurken dalgalaniyor. Yazili durum (ve ekran
 * okuyucu icin `aria-live`) ayni bilgiyi tasiyor; renk tek basina anlam
 * tasimiyor.
 */
export function CallScreen({
  snapshot,
  model,
  startedAt,
  onEnd,
  onToggleMute,
  onFinishTurn,
  voice,
  onModelChange,
  onVoiceChange,
}: {
  snapshot: CallSnapshot;
  model: string | null;
  startedAt: number;
  onEnd(): void;
  onToggleMute(): void;
  onFinishTurn(): void;
  voice: TtsVoice;
  onModelChange(model: string | null): void;
  onVoiceChange(voice: TtsVoice): void;
}) {
  const { t } = useTranslation();
  const [now, setNow] = useState(() => Date.now());
  const endButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    endButton.current?.focus();
  }, []);

  const { state, muted, level, heard, reply, error } = snapshot;
  const ended = state === "ended";
  const statusKey =
    muted && state === "listening" ? "call.state.muted" : `call.state.${state}`;
  // Ses seviyesi kureyi en fazla %35 buyutuyor; yalnizca kullanici konusurken.
  const listening = state === "listening" || state === "hearing";
  const scale = listening && !muted ? 1 + Math.min(level * 6, 0.35) : 1;
  const replyTail = reply.length > 260 ? `…${reply.slice(-260)}` : reply;

  return createPortal(
    <div
      className="call-screen"
      role="dialog"
      aria-modal="true"
      aria-labelledby="call-title"
    >
      <header className="call-head">
        <h2 id="call-title">{t("call.title")}</h2>
        {!ended && (
          <span className="call-meta">
            {formatClock((now - startedAt) / 1000)}
          </span>
        )}
        {/* Gorusme SURERKEN degistirilebilir; bir sonraki turdan itibaren
            gecerli (kullanici istegi). Soylenmekte olan yanit etkilenmez. */}
        {!ended && (
          <div className="call-options">
            <div className="call-option">
              <span className="call-option-label" aria-hidden="true">
                {t("call.model.label")}
              </span>
              <SegmentedControl<ModelChoice>
                options={[
                  { value: "reason", label: t("call.model.reason") },
                  { value: "fast", label: t("call.model.fast") },
                ]}
                value={model === "fast" ? "fast" : "reason"}
                onChange={(next) =>
                  onModelChange(next === "fast" ? "fast" : null)
                }
                aria-label={t("call.model.label")}
              />
            </div>
            <div className="call-option">
              <span className="call-option-label" aria-hidden="true">
                {t("call.voice.label")}
              </span>
              <SegmentedControl<TtsVoice>
                options={[
                  {
                    value: "chatterbox",
                    label: t("settings.voice.chatterbox"),
                  },
                  { value: "system", label: t("settings.voice.system") },
                ]}
                value={voice}
                onChange={onVoiceChange}
                aria-label={t("call.voice.label")}
              />
            </div>
          </div>
        )}
      </header>

      <div className="call-stage">
        <div
          className={`call-orb call-orb-${muted && listening ? "muted" : state}`}
          style={{ "--call-scale": scale } as CSSProperties}
          aria-hidden="true"
        >
          <span className="call-orb-ring" />
          <span className="call-orb-core" />
        </div>
        <p className="call-status" aria-live="polite">
          {ended && error ? t(error) : t(statusKey)}
        </p>
        {!ended && error && (
          <p className="call-error" role="status">
            {t(error)}
          </p>
        )}
      </div>

      <div className="call-captions" aria-live="polite">
        {heard && (
          <p className="call-heard">
            <span>{t("call.you")}</span> {heard}
          </p>
        )}
        {replyTail && <p className="call-reply">{replyTail}</p>}
        {!heard && !ended && <p className="call-hint">{t("call.hint")}</p>}
      </div>

      <div className="call-controls">
        {!ended && (
          <button
            type="button"
            className={muted ? "call-btn call-btn-muted" : "call-btn"}
            onClick={onToggleMute}
            aria-pressed={muted}
          >
            <span className="call-btn-icon">
              {muted ? <MicOffIcon /> : <MicOnIcon />}
            </span>
            <span className="call-btn-label">
              {muted ? t("call.unmute") : t("call.mute")}
            </span>
          </button>
        )}
        {state === "hearing" && !muted && (
          <button type="button" className="call-btn" onClick={onFinishTurn}>
            <span className="call-btn-icon">
              <CheckIcon />
            </span>
            <span className="call-btn-label">{t("call.done")}</span>
          </button>
        )}
        <button
          ref={endButton}
          type="button"
          className="call-btn call-btn-end"
          onClick={onEnd}
        >
          <span className="call-btn-icon">
            <HangUpIcon />
          </span>
          <span className="call-btn-label">
            {ended ? t("call.close") : t("call.end")}
          </span>
        </button>
      </div>
    </div>,
    document.body,
  );
}

function MicOnIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <rect
        x="9"
        y="3"
        width="6"
        height="11"
        rx="3"
        stroke="currentColor"
        strokeWidth="1.8"
      />
      <path
        d="M5 11a7 7 0 0 0 14 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function MicOffIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M15 10V6a3 3 0 0 0-5.7-1.3M9 9v2a3 3 0 0 0 4.9 2.3M5 11a7 7 0 0 0 11.5 5.4M19 11a7 7 0 0 1-.6 2.8M12 18v3M4 4l16 16"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="m5 12.5 4.5 4.5L19 7.5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HangUpIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M3.5 14.2c4.7-4.3 12.3-4.3 17 0l-1.9 2.4a1.2 1.2 0 0 1-1.5.3l-2.3-1.2a1.2 1.2 0 0 1-.6-1.2l.2-1.6a11 11 0 0 0-4.8 0l.2 1.6a1.2 1.2 0 0 1-.6 1.2l-2.3 1.2a1.2 1.2 0 0 1-1.5-.3l-1.9-2.4Z"
        fill="currentColor"
      />
    </svg>
  );
}
